import React, { useEffect, useState } from "react";
import { api, formatMXN } from "../lib/api";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Trophy, Medal, Crown, Sparkles } from "lucide-react";

function currentMonthKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function monthLabel(s) {
  if (!s) return "—";
  const [y, m] = s.split("-");
  const names = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
  return `${names[parseInt(m, 10) - 1]} ${y}`;
}

function PositionBadge({ pos }) {
  if (pos === 1) {
    return (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-300 to-amber-500 grid place-items-center shadow-lg shrink-0">
        <Crown className="w-6 h-6 text-white" />
      </div>
    );
  }
  if (pos === 2) {
    return (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-zinc-300 to-zinc-400 grid place-items-center shadow-md shrink-0">
        <Medal className="w-6 h-6 text-white" />
      </div>
    );
  }
  if (pos === 3) {
    return (
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 grid place-items-center shadow-md shrink-0">
        <Medal className="w-6 h-6 text-white" />
      </div>
    );
  }
  return (
    <div className="w-12 h-12 rounded-full bg-[#F5F3FF] border border-[#DDD6FE] grid place-items-center shrink-0">
      <span className="font-display text-lg text-[#5B21B6]">{pos}</span>
    </div>
  );
}

/**
 * Ranking card.
 * Props:
 *  - mode: "admin" | "promoter"
 *  - currentPromoterId (only when mode==="promoter"): id of logged-in promoter
 *  - compact (boolean): hides month picker (admin uses true if embedded; default false)
 */
export default function PromoterRanking({ mode = "admin", compact = false }) {
  const [month, setMonth] = useState(currentMonthKey());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (m) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/promoter-ranking?month=${m}&limit=10`);
      setData(data);
    } catch {
      setData(null);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(month); /* eslint-disable-next-line */ }, [month]);

  const top = data?.top || [];
  const my = data?.my_rank;

  return (
    <div className="bg-white border border-border rounded-2xl p-6" data-testid="promoter-ranking-card">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-200 via-amber-300 to-amber-500 grid place-items-center">
            <Trophy className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-display text-2xl tracking-tight">Top promotores del mes</h3>
            <p className="text-xs text-muted-foreground">
              Ranking por comisiones generadas en {monthLabel(month)}.
            </p>
          </div>
        </div>
        {!compact && (
          <div className="flex items-center gap-2">
            <Label className="text-xs text-muted-foreground">Mes:</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" data-testid="ranking-month-input" />
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-muted-foreground text-sm py-8 text-center">Cargando ranking…</div>
      ) : top.length === 0 ? (
        <div className="text-sm text-muted-foreground py-10 text-center border-2 border-dashed border-border rounded-xl" data-testid="ranking-empty">
          Aún no hay comisiones en {monthLabel(month)}. ¡Sé el primero!
        </div>
      ) : (
        <>
          {/* Podio: top 3 cards */}
          <div className="grid sm:grid-cols-3 gap-3 mb-5">
            {top.slice(0, 3).map((r) => {
              const accent = r.position === 1
                ? "from-amber-50 to-amber-100 border-amber-200"
                : r.position === 2
                ? "from-zinc-50 to-zinc-100 border-zinc-200"
                : "from-orange-50 to-orange-100 border-orange-200";
              const isMe = mode === "promoter" && my && r.promoter_id === my.promoter_id;
              return (
                <div
                  key={r.promoter_id}
                  className={`rounded-2xl border bg-gradient-to-br ${accent} p-4 flex flex-col gap-2 ${isMe ? "ring-2 ring-[#7C3AED]" : ""}`}
                  data-testid={`ranking-podium-${r.position}`}
                >
                  <div className="flex items-center gap-3">
                    <PositionBadge pos={r.position} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500">#{r.position}</div>
                      <div className="font-medium truncate">
                        {r.name}{isMe && <span className="ml-1.5 text-[10px] text-[#7C3AED] font-bold">(TÚ)</span>}
                      </div>
                    </div>
                  </div>
                  <div className="font-display text-2xl">{formatMXN(r.total_amount)}</div>
                  <div className="text-xs text-zinc-600">
                    {r.businesses_paid_this_month} comercio(s) pagaron · {r.active_affiliates} activos
                  </div>
                </div>
              );
            })}
          </div>

          {/* Resto del top 10 */}
          {top.length > 3 && (
            <div className="space-y-2" data-testid="ranking-list">
              {top.slice(3).map((r) => {
                const isMe = mode === "promoter" && my && r.promoter_id === my.promoter_id;
                return (
                  <div
                    key={r.promoter_id}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border ${isMe ? "border-[#7C3AED] bg-[#F5F3FF]" : "border-border bg-white hover:bg-[#FAFAFA]"} transition-colors`}
                    data-testid={`ranking-row-${r.position}`}
                  >
                    <PositionBadge pos={r.position} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">
                        {r.name}{isMe && <span className="ml-1.5 text-[10px] text-[#7C3AED] font-bold">(TÚ)</span>}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {r.businesses_paid_this_month} comercio(s) este mes · {r.active_affiliates} activos
                      </div>
                    </div>
                    <div className="font-display text-xl">{formatMXN(r.total_amount)}</div>
                  </div>
                );
              })}
            </div>
          )}

          {/* My position banner (only for promoters when not already in top) */}
          {mode === "promoter" && my && my.position && my.position > 10 && (
            <div className="mt-5 bg-gradient-to-r from-[#7C3AED] to-[#5B21B6] text-white rounded-2xl p-4 flex items-center justify-between gap-3" data-testid="ranking-my-position-out-of-top">
              <div className="flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-amber-300" />
                <div>
                  <div className="text-xs text-white/80">Tu posición</div>
                  <div className="font-display text-2xl">#{my.position} — {formatMXN(my.total_amount)}</div>
                </div>
              </div>
              <div className="text-xs text-white/80 text-right">
                ¡Afilia más comercios para subir al podio!
              </div>
            </div>
          )}

          {mode === "promoter" && my && !my.position && (
            <div className="mt-5 bg-[#F5F3FF] border border-[#DDD6FE] rounded-2xl p-4 flex items-center justify-between gap-3" data-testid="ranking-my-position-unranked">
              <div className="flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-[#7C3AED]" />
                <div>
                  <div className="text-xs text-muted-foreground">Aún no apareces en el ranking</div>
                  <div className="text-sm font-medium">Tienes {my.active_affiliates} comercio(s) activo(s). Cuando paguen, sumarás puntos.</div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
